// Drainer de la outbox (Paso 6 / Fase 4 del plan offline).
//
// Toma las operaciones pendientes de la outbox y las manda en un solo lote a
// POST /api/sync/batch. Segun el resultado por operacion marca cada fila:
//   ok       -> markSynced (guarda serverId; idempotente, no se reintenta)
//   conflict -> markConflict (rechazo deterministico, requiere intervencion)
//   error    -> markError (transitorio, reintenta hasta MAX_RETRIES)
//
// Al final invalida las queries de las entidades afectadas para que la lista
// refetchee y reemplace la fila optimista (`pending-...`) por la real (serverId).
//
// Se dispara best-effort desde varias fuentes (onSuccess de una mutation, el
// evento `online`, el boot). El guard isDraining evita lotes simultaneos.
import {
    getPending,
    markInFlight,
    markSynced,
    markError,
    markConflict,
    markPending,
    retry,
    discard,
    recordServerId,
    getIdMap,
    updatePayload,
} from "$lib/outbox/outboxApi";
import { batchSync } from "$lib/java/sync/syncback";
import { queryClient } from "$lib/query/queryClient";
import { getUser } from "$lib/userstorage/usersotrage";
import { getScopedToken } from "$lib/java/auth/refreshSession";
import { loadStorageEstablecimiento } from "$lib/java/establecimientos/establecimientostorage";
import { getMisPermisos } from "$lib/java/permisos/permisosback";
import { noPermission, fail } from "$lib/stringutil/lib";
import { isOnline } from "$lib/sync/networkStatus";

// Entidad del batch -> etiqueta legible (para los avisos de rebote).
const ENTITY_LABEL = {
    comment: "la observación",
    weight: "el pesaje",
    treatmentEvent: "el tratamiento",
    pregnancyCheck: "el tacto",
    birth: "el nacimiento",
    service: "el servicio",
    animal: "el animal",
    movement: "el movimiento",
    death: "la baja por muerte",
    sale: "la baja por venta",
    treatmentType: "el tipo de tratamiento",
};

// entity del batch -> prefijo de queryKey a invalidar. Invalidamos por prefijo
// (sin estId) para no depender del establishment actual: ["observations"]
// matchea ["observations", <cualquier estId>].
const INVALIDATE_PREFIX = {
    comment: ["observations"],
    // Un pesaje nuevo cambia el currentWeight del animal → invalidamos también animals.
    weight: [["weights"], ["animals"]],
    treatmentEvent: ["treatments"],
    treatmentType: ["treatmentTypes"],
    // Un tacto nuevo/editado actualiza el estado reproductivo del animal
    // (applyReproductiveStatus, backend) → invalidamos también animals.
    pregnancyCheck: [["pregnancyChecks"], ["animals"]],
    // Alta/edición de un nacimiento cambia madre/padre/fecha del animal cría
    // asociado; eliminarlo da de baja en cascada a ese animal (si tiene uno,
    // ver BirthServiceImpl.delete) → invalidamos también animals y, como puede
    // sacar un animal activo de su lote/rodeo, también esos conteos.
    birth: [["births"], ["animals"], ["lots"], ["herds"]],
    service: ["services"],
    // Alta/edición/baja de animal cambia el CONTEO de animales del lote y del
    // rodeo (animalCount de /lots/all y /herds/all) → invalidamos también esas
    // listas. Sin esto, un alta simple (sin parto ni peso, que es la que sale
    // por la outbox) dejaba el contador de la pantalla de lotes/rodeos con el
    // valor viejo hasta que venciera el staleTime de 5 min. Invalidar acá y no
    // en el form es lo correcto: acá el batch YA confirmó en el server, así que
    // el refetch no corre una carrera contra el POST /sync/batch.
    animal: [["animals"], ["lots"], ["herds"]],
    lot: ["lots"],
    herd: ["herds"],
    // Un movimiento (baja, cambio de lote/rodeo/establecimiento) aplica su
    // efecto sobre el animal (applyMovementEffect, backend) → invalidamos
    // también animals, igual que weight/pregnancyCheck/death/sale. Y como puede
    // mover el animal de lote/rodeo, también los conteos.
    movement: [["movements"], ["animals"], ["lots"], ["herds"]],
    // Dar de baja toca el estado del animal → invalidamos la lista de animales
    // y los conteos (el animal deja de contar como activo en su lote/rodeo).
    death: [["animals"], ["lots"], ["herds"]],
    sale: [["animals"], ["lots"], ["herds"]],
};

// Una FK temporal a un registro creado offline tiene la forma
// "pending-<clientUuid>". Al confirmarse el padre, el drainer aprende
// clientUuid→serverId (idMap) y acá reemplaza esas FK por el id real antes de
// mandar el hijo. Recorre el payload en PROFUNDIDAD (strings sueltos, arrays y
// objetos anidados) porque la FK aparece en varias formas según la entidad:
// `animalId` (weight/service/birth), `animalIds:[...]` (treatmentEvent/
// pregnancyCheck), `fathers:[{fatherId}]` (service), `motherId`/`fatherId`
// (birth). Devuelve el payload (nueva referencia si cambió), los uuids que NO se
// pudieron resolver (padre aún sin sincronizar → diferir la op) y si cambió.
const PENDING_REF = /^pending-([0-9a-fA-F-]{36})$/;
function resolvePendingRefs(payload, idMap) {
    const unresolved = [];
    let changed = false;

    function walk(value) {
        if (typeof value === "string") {
            const m = PENDING_REF.exec(value);
            if (!m) return value;
            const serverId = idMap.get(m[1]);
            if (serverId != null) {
                changed = true;
                return serverId;
            }
            unresolved.push(m[1]);
            return value;
        }
        if (Array.isArray(value)) {
            return value.map(walk);
        }
        if (value && typeof value === "object") {
            const out = {};
            for (const [k, v] of Object.entries(value)) out[k] = walk(v);
            return out;
        }
        return value;
    }

    const resolved = walk(payload);
    return { payload: changed ? resolved : payload, unresolved, changed };
}

let isDraining = false;
// Si llega una llamada a drainOutbox mientras ya hay un lote en vuelo, NO la
// descartamos (eso dejaba huerfanas las ops encoladas durante el fetch -p.ej. el
// loop del borrado masivo: enqueue+drain de a uno; el 2do drain caia en el guard
// y su fila quedaba "pending" para siempre aunque hubiera red). En vez de eso
// pedimos "re-correr al terminar" y el loop de abajo vuelve a drenar lo nuevo.
let rerunRequested = false;

export async function drainOutbox() {
    if (!isOnline()) return;
    if (isDraining) {
        rerunRequested = true; // se procesara en cuanto termine el lote actual
        return;
    }
    isDraining = true;

    try {
        do {
            rerunRequested = false;
            const ok = await drainOnce();
            // drainOnce devuelve false ante fallo de red/sesion (todo volvio a
            // pending). No reintentamos en bucle: esperamos el proximo trigger
            // (evento online / re-login / refetch) para no hacer busy-loop.
            if (!ok) break;
        } while (rerunRequested);
    } finally {
        isDraining = false;
    }
}

// Drena UN ciclo. Multi-establecimiento: agrupa las pendientes por
// establishmentId y drena cada grupo con un access scoped a ESE establecimiento
// (canjeado del refresh token). Asi las ops cargadas en distintas estancias
// llegan cada una a la suya, aunque hoy el activo sea otro. Devuelve true si
// proceso todos los grupos; false si alguno fallo por red/sesion (corta el bucle).
async function drainOnce() {
    const pending = await getPending();
    if (pending.length === 0) return true;

    // Mapa clientUuid→serverId aprendido de CREATEs ya confirmados, para resolver
    // las FK temporales "pending-<clientUuid>" de los hijos antes de mandarlos.
    const idMap = await getIdMap();

    // Agrupar por establishmentId. Filas viejas (Fase 2, sin el campo) caen en
    // el establecimiento activo.
    const activeEstId = loadStorageEstablecimiento()?.id ?? null;
    const groups = new Map();
    for (const row of pending) {
        const estId = row.establishmentId ?? activeEstId;
        if (!groups.has(estId)) groups.set(estId, []);
        groups.get(estId).push(row);
    }

    let allOk = true;
    for (const [estId, rows] of groups) {
        const ok = await drainGroup(estId, rows, activeEstId, idMap);
        if (!ok) allOk = false;
    }
    return allOk;
}

// Drena las operaciones de UN establecimiento con su token scoped.
async function drainGroup(estId, rows, activeEstId, idMap) {
    // Token scoped al establecimiento del grupo. El activo reusa el de sesión;
    // los demás canjean on-demand (getScopedToken devuelve también el motivo).
    let token;
    if (estId != null && String(estId) === String(activeEstId)) {
        token = getUser().token;
    } else {
        const r = await getScopedToken(estId);
        if (r.reason === "removed") {
            // Te sacaron del establecimiento: estos cambios nunca van a entrar.
            await descartarGrupoRemovido(estId, rows);
            return true; // grupo resuelto (descartado); no corta el bucle
        }
        if (r.reason !== "ok") {
            // network / invalid: no es rechazo del dato; reintentar luego.
            for (const row of rows) await markPending(row.id);
            return false;
        }
        token = r.token;
    }

    // Resolver las FK temporales "pending-<clientUuid>" con el idMap y separar
    // ENVIABLES de DIFERIDAS. Una fila cuya FK apunta a un padre que todavía no
    // sincronizó (uuid ausente del idMap) se DIFIERE: queda pending y se reintenta
    // en el próximo ciclo, cuando el padre ya confirmó (mandarla ahora la haría
    // rebotar en el backend con "Long inválido"). El padre (sin FK pendientes) sí
    // va en este ciclo; al confirmarse, su serverId entra al idMap y desbloquea al
    // hijo. Las que se resuelven persisten su payload corregido (autocontenidas).
    const ready = [];
    let deferred = 0;
    for (const row of rows) {
        const { payload, unresolved, changed } = resolvePendingRefs(row.payload, idMap);
        if (unresolved.length > 0) {
            deferred++;
            continue;
        }
        if (changed) {
            await updatePayload(row.id, payload);
            row.payload = payload;
        }
        ready.push(row);
    }
    if (ready.length === 0) return true; // todo diferido este ciclo; nada que mandar

    // Marcar in_flight antes de mandar: si la app crashea durante el fetch,
    // al reabrir vemos filas in_flight y se pueden recuperar a pending.
    await Promise.all(ready.map((row) => markInFlight(row.id)));

    const operations = ready.map((row) => ({
        clientUuid: row.clientUuid,
        entity: row.entity,
        operation: row.operation,
        resourceId: row.resourceId ?? null,
        payload: row.payload,
    }));

    let result;
    try {
        result = await batchSync(operations, token);
    } catch (err) {
        // fetch que rechaza (sin red), 401, o error del servidor (5xx, que desde
        // el fix de res.ok llega como throw y no como body basura): nada de eso
        // es un rechazo del DATO; vuelve a pending sin consumir reintentos.
        for (const row of ready) await markPending(row.id);
        return false;
    }

    const affected = new Set();
    let permisosRefrescados = false;
    let syncedCreate = false;
    for (const r of result.results ?? []) {
        const row = ready.find((p) => p.clientUuid === r.clientUuid);
        if (!row) continue;
        affected.add(row.entity);

        if (r.status === "ok") {
            // CREATE confirmado: registrar clientUuid→serverId para resolver las
            // FK de los hijos que dependen de él (idMap persistente + en memoria
            // para este mismo ciclo de drenado).
            if (row.operation === "CREATE" && r.serverId != null) {
                await recordServerId(row.clientUuid, r.serverId);
                idMap.set(row.clientUuid, r.serverId);
                syncedCreate = true;
            }
            await markSynced(row.id);
        } else if (r.status === "conflict") {
            await handleConflict(row, r);
            // Ante el primer forbidden del grupo, refrescar permisos una vez para
            // que el gating deje de ofrecer lo que no se puede (corta el loop).
            if (r.errorCode === "forbidden" && !permisosRefrescados) {
                permisosRefrescados = true;
                refrescarPermisos(estId);
            }
        } else {
            await markError(row.id, r.errorMessage ?? "error");
        }
    }

    // Si diferimos hijos y en este ciclo se confirmó al menos un CREATE (padre),
    // pedir otra pasada: ahora el idMap puede resolver a esos hijos. Solo se
    // re-corre ante progreso real (un CREATE synced) → no hay busy-loop.
    if (deferred > 0 && syncedCreate) rerunRequested = true;

    // Refetch de las listas tocadas para reemplazar lo optimista por lo real.
    // prefix puede ser un array simple (["weights"]) o un array de arrays
    // ([["weights"], ["animals"]]) cuando una entidad toca varios query keys.
    for (const entity of affected) {
        const prefix = INVALIDATE_PREFIX[entity];
        if (!prefix) continue;
        const prefixes = Array.isArray(prefix[0]) ? prefix : [prefix];
        for (const p of prefixes) {
            queryClient.invalidateQueries({ queryKey: p });
        }
    }
    return true;
}

// Deshace el cambio optimista de una operación rechazada por el server.
// DELETE: la fila ya se sacó del cache (sin tag), así que no hay nada que
// "filtrar" — hay que REINSERTAR la que se guardó en removedRow (ver
// deleteOffline). Sin esto, un DELETE rechazado (ej. sin permiso) dejaba la
// fila desaparecida de la lista aunque nunca se borró en el servidor.
// CREATE: la fila optimista está tageada con _clientUuid → se filtra.
function revertOptimistic(row) {
    if (!row?.queryKey) return;
    if (row.operation === "DELETE") {
        if (!row.removedRow) return;
        queryClient.setQueryData(row.queryKey, (old = []) =>
            Array.isArray(old) && !old.some((x) => x.id === row.removedRow.id)
                ? [row.removedRow, ...old]
                : old,
        );
        return;
    }
    if (row.clientUuid) {
        queryClient.setQueryData(row.queryKey, (old) =>
            Array.isArray(old)
                ? old.filter((x) => x._clientUuid !== row.clientUuid)
                : old,
        );
    }
}

// Rechazo determinístico por-item: queda "rechazado" en el drawer (con el
// errorCode), revierte la fila optimista (sale de la lista) y avisa al usuario.
async function handleConflict(row, r) {
    await markConflict(row.id, r.errorMessage ?? r.errorCode ?? "conflict", r.errorCode);
    revertOptimistic(row);
    const ent = ENTITY_LABEL[row.entity] ?? "el registro";
    if (r.errorCode === "forbidden") {
        noPermission(`Se descartó ${ent}: no tenés permiso para esa acción.`);
    } else if (r.errorCode === "plan_limit") {
        // NO se descarta: la fila queda en la outbox como "Rechazado" y es
        // reintentable (esReintentable del SyncDrawer no lista plan_limit), así
        // que cuando el plan se amplíe entra con "Reintentar". Sale de la lista
        // porque en el servidor no se creó, pero el dato no se pierde.
        fail(
            `No se pudo sincronizar ${ent}: ${r.errorMessage ?? "alcanzaste el límite de tu plan"} ` +
                `Lo guardamos en pendientes para reintentar.`,
        );
    } else {
        fail(`Se descartó ${ent}: el dato ya no existe o es inválido.`);
    }
}

// Auto-refresco de permisos ante forbidden (re-baja y re-cachea). Best-effort.
// Va por getMisPermisos porque es el cache que consumen los gates de las
// pantallas; getColabPermisos quedo solo para la admin de colaboradores.
function refrescarPermisos(estId) {
    if (estId) getMisPermisos(estId).catch(() => {});
}

// Te sacaron del establecimiento: descartar el grupo entero con aviso prominente.
async function descartarGrupoRemovido(estId, rows) {
    for (const row of rows) {
        revertOptimistic(row);
        await discard(row.id);
    }
    noPermission(
        `Ya no tenés acceso a ${nombreEst(estId)}. Se descartaron ${rows.length} cambio(s) sin sincronizar.`,
    );
}

function nombreEst(estId) {
    const cab = loadStorageEstablecimiento();
    if (cab && String(cab.id) === String(estId)) {
        return cab.nombre || "ese establecimiento";
    }
    const ests = getUser()?.establishments || [];
    const found = ests.find((e) => String(e.establishmentId) === String(estId));
    return found?.establishmentName || "ese establecimiento";
}

// --- Acciones manuales del usuario desde la UI de sync (Paso 8) ---

// Reintento manual de una fila en error/conflict: la vuelve a pending y dispara
// un drenado best-effort. Si sigue sin red, queda pending hasta la proxima.
export async function retryOperation(id) {
    await retry(id);
    drainOutbox().catch(() => {});
}

// Descartar una fila: la borra de la outbox Y limpia su fila optimista del cache
// de la lista (matcheando por _clientUuid). Sin esto, una operacion descartada
// dejaria su fila optimista colgada para siempre con _pendingSync.
export async function discardOperation(id) {
    const row = await discard(id);
    if (row?.queryKey && row?.clientUuid) {
        queryClient.setQueryData(row.queryKey, (old) =>
            Array.isArray(old)
                ? old.filter((r) => r._clientUuid !== row.clientUuid)
                : old,
        );
    }
}
